import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { axiosInstance } from '@/App';
import { User, Lock, CreditCard, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const Account = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    fetchAccountData();
  }, []);

  const fetchAccountData = async () => {
    try {
      const response = await axiosInstance.get('/user/account');
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching account data:', error);
      toast.error('Fehler beim Laden der Kontodaten');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Die neuen Passwörter stimmen nicht überein');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error('Das neue Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setChangingPassword(true);
    try {
      await axiosInstance.post('/user/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      
      toast.success('Passwort erfolgreich geändert!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelingSubscription(true);
    try {
      await axiosInstance.post('/user/cancel-subscription');
      
      toast.success('Abo erfolgreich gekündigt. Sie können es noch bis zum Monatsende nutzen.');
      setShowCancelConfirm(false);
      fetchAccountData(); // Refresh data
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Kündigen des Abos');
    } finally {
      setCancelingSubscription(false);
    }
  };

  const getSubscriptionDisplay = (status, type) => {
    if (!user && !status) return '';
    
    const currentStatus = status || user.subscription_status;
    const currentType = type || user.subscription_type;
    
    if (currentStatus === 'lifetime' || currentType === 'lifetime') {
      return 'Lifetime (Einmalige Zahlung - 79,99€)';
    }
    if (currentType === 'starter') {
      return 'Starter Abo (19,99€/Monat)';
    }
    switch (currentStatus) {
      case 'active':
        return 'Aktives Abo';
      case 'canceled':
        return 'Gekündigt (Aktiv bis Monatsende)';
      case 'inactive':
      default:
        return 'Kostenlos';
    }
  };

  const getSubscriptionIcon = () => {
    if (!user) return null;
    
    switch (user.subscription_status) {
      case 'lifetime':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'active':
        return <CheckCircle className="w-6 h-6 text-blue-500" />;
      case 'canceled':
        return <AlertCircle className="w-6 h-6 text-orange-500" />;
      case 'inactive':
      default:
        return <XCircle className="w-6 h-6 text-[rgba(245,245,247,0.5)]" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-[rgba(245,245,247,0.7)]">Lädt Kontoinformationen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold mb-6">Mein Konto</h1>

      {/* Account Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Account-Informationen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-[rgba(245,245,247,0.7)]">Benutzername</Label>
            <p className="text-lg font-medium">{user?.username}</p>
          </div>
          <div>
            <Label className="text-[rgba(245,245,247,0.7)]">E-Mail</Label>
            <p className="text-lg font-medium">{user?.email}</p>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Abo-Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {getSubscriptionIcon()}
            <div>
              <p className="text-lg font-medium">{getSubscriptionDisplay(user?.subscription_status, user?.subscription_type)}</p>
              {user?.subscription_status === 'active' && (
                <p className="text-sm text-[rgba(245,245,247,0.7)]">Nächste Abrechnung am {user?.next_billing_date || 'TBD'}</p>
              )}
              {user?.subscription_status === 'canceled' && (
                <p className="text-sm text-orange-600">Ihr Zugang endet am {user?.subscription_end_date || 'Monatsende'}</p>
              )}
            </div>
          </div>

          {user?.subscription_status === 'active' && user?.subscription_type !== 'lifetime' && (
            <div className="pt-4 border-t">
              {!showCancelConfirm ? (
                <Button
                  variant="outline"
                  onClick={() => setShowCancelConfirm(true)}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  Abo kündigen
                </Button>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-bold text-red-900">Abo wirklich kündigen?</h4>
                      <p className="text-sm text-red-800 mt-1">
                        Sie können Ihr Abo noch bis zum Ende des aktuellen Abrechnungszeitraums nutzen. 
                        Danach wird es automatisch deaktiviert.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleCancelSubscription}
                      disabled={cancelingSubscription}
                      variant="destructive"
                    >
                      {cancelingSubscription ? 'Wird gekündigt...' : 'Ja, Abo kündigen'}
                    </Button>
                    <Button
                      onClick={() => setShowCancelConfirm(false)}
                      variant="outline"
                    >
                      Abbrechen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Passwort ändern
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">Aktuelles Passwort</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="newPassword">Neues Passwort</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                required
                minLength={8}
              />
              <p className="text-xs text-[rgba(245,245,247,0.6)] mt-1">Mindestens 8 Zeichen</p>
            </div>
            <div>
              <Label htmlFor="confirmPassword">Neues Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                required
              />
            </div>
            <Button 
              type="submit" 
              disabled={changingPassword}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {changingPassword ? 'Wird geändert...' : 'Passwort ändern'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Account;
